import React, { memo, useState } from 'react';

import Link from 'next/link';

import { Box, Divider, Typography } from '@explorer/core';
import { trailingUrl } from '@explorer/helpers';
import { useDialog } from '@explorer/hooks/useDialog/useDialog';
import { ControlClientInfoUser } from '@explorer/open-api-client';
import { alpha, Theme, useTheme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { useIntl } from 'react-intl';

import { AssociatedUserModal } from './AssociatedUserModal';
import { AssociatedUserRow } from './AssociatedUserRow';

const useStyles = () =>
  makeStyles(
    (theme: Theme) =>
      createStyles({
        scroll: {
          height: '395px',
          overflowY: 'scroll',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[500],
            borderRadius: 2,
          },
        },
      }),
    { index: 1 },
  )();

export const AssociatedUsersList = memo(function AssociatedUsersList({
  associatedUsers,
  linkToUserPage = false,
}: AssociatedUsersListProps) {
  const { palette } = useTheme();
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { open, openDialog, closeDialog } = useDialog();
  const [userKey, setUserKey] = useState<string>('');

  const handleClickOpenModal = (key: string) => {
    setUserKey(key);
    openDialog();
  };

  return (
    <Box
      className={classes.scroll}
      sx={{
        border: `1px solid ${alpha(palette.text.primary, 0.23)}`,
        borderRadius: 2,
      }}
    >
      <Typography
        variant="bodyS"
        fontWeight="bold"
        color="textPrimary"
        sx={{
          position: 'absolute',
          mt: -1,
          ml: 1,
          zIndex: 2,
          bgcolor: palette.background.paper,
          px: 1,
        }}
      >
        {formatMessage({ id: 'admin.users-found' })} ({associatedUsers.length}
        ):
      </Typography>
      <Box px={1} py={2}>
        {associatedUsers?.map((user: ControlClientInfoUser) => (
          <React.Fragment key={user.user_id}>
            {linkToUserPage && user.email ? (
              <Link
                href={trailingUrl('/users', { email: user.email })}
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                onClick={(event) => event.stopPropagation()}
              >
                <AssociatedUserRow
                  user={user}
                  palette={palette}
                  linkToUserPage
                />
              </Link>
            ) : (
              <Box
                onClick={() => handleClickOpenModal(user.user_id!)}
                style={{ cursor: 'pointer' }}
              >
                <AssociatedUserRow user={user} palette={palette} />
              </Box>
            )}
            {!linkToUserPage && user?.user_id === userKey ? (
              <AssociatedUserModal
                user={user}
                onClose={closeDialog}
                open={open}
                classes={classes}
              />
            ) : null}
            <Divider sx={{ mx: 3 }} />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
});

export interface AssociatedUsersListProps {
  readonly associatedUsers: ControlClientInfoUser[];
  /** Open the user page instead of the details modal. */
  readonly linkToUserPage?: boolean;
}
